import type { ReviewClaim } from "@patent-studio/api-types";
import { AlignLeft } from "lucide-react";
import { ClaimList } from "@/components/patterns/ThesisLists";
import {
  DrawnElements,
  useDrawnElements,
} from "../../claim-strategy/drawn-elements";
import { type EditTarget, ReviewCard } from "./ReviewCard";

/** The claims of each category in the set's order, the categories in the order they first appear. */
function byCategory(claims: ReviewClaim[]): [string, ReviewClaim[]][] {
  const groups = new Map<string, ReviewClaim[]>();
  for (const claim of claims) {
    groups.set(claim.category, [...(groups.get(claim.category) ?? []), claim]);
  }
  return [...groups];
}

function counts(claims: ReviewClaim[]): string {
  const independent = claims.filter((c) => c.kind === "independent").length;
  return `${String(independent)} independent · ${String(claims.length - independent)} dependent`;
}

/** One category's claims, each named part with the numeral it is drawn with, as Claim Strategy shows it. */
function Claims({ claims }: { claims: ReviewClaim[] }) {
  const { references } = useDrawnElements();
  return <ClaimList claims={claims} references={references} />;
}

/** The claim set by category: each independent claim in its words, its dependents folded beneath it. */
export function ClaimsCard({
  taskId,
  claims,
  edit,
}: {
  taskId: string;
  claims: ReviewClaim[];
  edit: EditTarget;
}) {
  return (
    <ReviewCard icon={<AlignLeft />} title="Claim strategy" edit={edit}>
      {claims.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No claim set has been drafted yet.
        </p>
      ) : (
        <DrawnElements taskId={taskId}>
          <div className="space-y-5">
            {byCategory(claims).map(([category, held]) => (
              <div key={category}>
                <div className="mb-2 flex flex-wrap items-baseline gap-2.5">
                  <h3 className="text-[15px] font-bold">{category}</h3>
                  <span className="text-xs text-subtle-foreground">
                    {counts(held)}
                  </span>
                </div>
                <Claims claims={held} />
              </div>
            ))}
          </div>
        </DrawnElements>
      )}
    </ReviewCard>
  );
}
